/**
 * publishFingerprints.ts
 *
 * PLAT-10023 / PLAT-10024 — staleness detection for `PublishRecord`s.
 *
 * A **fingerprint** is a per-field SHA-256 map (`PublishFingerprints`) capturing
 * the render-relevant state of one `(aspectRatio × language)` cell at publish
 * time. `isOutdated` recomputes the same map from the *current* story state and
 * compares: any differing field means the render no longer reflects the source.
 * A per-field map (not one combined hash) lets `whyOutdated` name the category
 * that changed for the staleness tooltip.
 *
 * Each slice hashes a **canonical** projection — only render-relevant fields, in
 * a fixed order — so incidental churn (object key order, provenance metadata,
 * signed-URL rotation, the per-cell baked caption language) never moves the hash
 * and only real edits do. There are no magic sentinels: an absent/empty input
 * canonicalises to `[]` / `null`, a stable constant that only ever compares
 * against itself.
 *
 * See `docs/plans/PLAT-10023-staleness-detection.md` for the slice contract.
 */

import { KARAOKE_TRACK_NUMBER } from '@src/editor/constants';
import { sha256 } from '@src/utility/sha256';

import type { IScene } from '@src/editor/models/layout/types';
import type { KaraokeData } from '@src/editor/types/karaoke';
import type {
  PublishFingerprints,
  PublishRecord,
} from '@src/types.ts/publishRecord';
import type { TranscriptElement } from '@src/types.ts/transcript';
import type { TranslationPhrase } from '@src/types.ts/translation';
import type { CompositionPhraseTranslation } from '@src/videoTranscriptionProcessor/translation/compositionTypes';

/**
 * The inputs one fingerprint is computed from. The three per-AR slices come from
 * the AR's own resources (`base`); the two per-language slices from the
 * `TranslationStore`. `null` / empty is valid (e.g. the original language has no
 * translation phrases) and canonicalises to a stable empty digest.
 */
export interface FingerprintInputs {
  /** The AR's composition (`base.videoSource`); normalised before hashing. */
  videoSource: Record<string, unknown>;
  /** The AR's own `transcriptionSnapshot.elements`. */
  transcriptElements: TranscriptElement[];
  /** The AR's karaoke data; projected to config (`language` stripped) + structural
   * clip fields + `syntheticBreaks` (per-language text excluded). `null` ⇒ no captions. */
  karaokeData: KaraokeData | null;
  /** Latest `getTranslation(L).phrases`; `null` ⇒ original language. */
  translationPhrases: TranslationPhrase[] | null;
  /** Composition-phrase map for L (`getCompositionPhrasesByNormalizedSource`). */
  compositionPhrases: ReadonlyMap<string, CompositionPhraseTranslation>;
  /** The AR's scene/layout segments (per-AR, loaded into the editor at render). */
  scenes: IScene[] | null;
}

// ---------------------------------------------------------------------------
// Canonicalisation helpers (local, small — not a shared abstraction)
// ---------------------------------------------------------------------------

/**
 * Deterministic JSON: object keys sorted recursively so key order can't move a
 * hash. Only needed for the two object-shaped slices (timeline, captions); the
 * array-shaped slices project to ordered tuples where plain order is stable.
 */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value) ?? 'null';
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  const obj = value as Record<string, unknown>;
  // Omit `undefined`-valued keys to match `JSON.stringify`'s object semantics —
  // otherwise `{ duration: undefined }` and `{}` would hash differently and
  // false-flag renders as outdated (we construct sources with `undefined`
  // fields in places, e.g. VideoCreatorStore).
  const keys = Object.keys(obj)
    .filter(k => obj[k] !== undefined)
    .sort();
  return `{${keys
    .map(k => `${JSON.stringify(k)}:${stableStringify(obj[k])}`)
    .join(',')}}`;
}

/**
 * Strip a URL's query string (presign signature + expiry rotate every publish).
 * Leaves `s3://` and non-URL strings untouched.
 */
function stripUrlQuery(value: string): string {
  if (!/^https?:\/\//i.test(value)) return value;
  const q = value.indexOf('?');
  return q === -1 ? value : value.slice(0, q);
}

/** Recursively strip signed-URL query strings from every string in the source. */
function stripSignedUrls(value: unknown): unknown {
  if (typeof value === 'string') return stripUrlQuery(value);
  if (Array.isArray(value)) return value.map(stripSignedUrls);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = stripSignedUrls(v);
    }
    return out;
  }
  return value;
}

/**
 * Normalise a video source for hashing: drop the baked karaoke/caption track
 * (its own `captions` slice owns it) and strip the signed-URL query strings that
 * rotate every publish. Two publishes of an unchanged timeline — differing only
 * by freshly signed URLs — must normalise identically.
 */
export function normalizeTimeline(
  videoSource: Record<string, unknown>,
): unknown {
  const elements = Array.isArray(videoSource.elements)
    ? (videoSource.elements as Record<string, unknown>[]).filter(
        el => el?.track !== KARAOKE_TRACK_NUMBER,
      )
    : videoSource.elements;
  return stripSignedUrls({ ...videoSource, elements });
}

/**
 * Render-affecting, language-independent clip fields (a strict **whitelist**). The
 * persisted clip is the fat shape, so everything not listed — the heavy
 * `transcriptTokens`, computed `allHiddenTranscriptTokenIds` /
 * `transcriptStartTime` / `transcriptEndTime`, the incidental clip `id`, and the
 * per-language derived text (`translatedText` / `translatedPhrases` /
 * `phraseOriginalIndices` / `siblingGroupId`) — is excluded by construction.
 */
const KARAOKE_CLIP_FIELDS = [
  'transcriptTokenIds',
  'hiddenTranscriptTokenIds',
  'ignoredByFiltersIds',
  'startSecondsOffset',
  'endSecondsOffset',
  'karaokeConfigId',
] as const;

/**
 * Project karaoke data to its render-affecting, **language-independent** state:
 * the default config (`configs.get(defaultConfigId)`, `language` stripped —
 * varies per baked cell, not a styling change) + `defaultConfigId` + an ordered
 * array of per-clip whitelist tuples (plus the clip's soft line breaks when it
 * has any — PLAT-11914; the caller passes the cell's own language variant, and
 * the translation slice doesn't cover them) + `syntheticBreaks` (projected to their
 * content, break `id` dropped like clip `id`). Both publish and view read the same
 * serialized `karaokeData`, so the projection is apples-to-apples and clip order
 * is stable. `null` when there are no captions.
 */
function projectKaraoke(karaokeData: KaraokeData | null): unknown {
  if (!karaokeData) return null;

  const rawConfig = karaokeData.defaultConfigId
    ? karaokeData.configs.get(karaokeData.defaultConfigId)
    : undefined;
  // `language` varies per baked cell (one config, re-produced per language in
  // place) and is not a styling change — strip it. `config` stays `unknown`
  // because the util hashes it opaquely (it doesn't depend on the schema).
  let config: unknown = null;
  if (rawConfig) {
    const { language: _language, ...rest } = rawConfig;
    void _language;
    config = rest;
  }

  const clips = (karaokeData.clips ?? []).map(clip => {
    const tuple: unknown[] = KARAOKE_CLIP_FIELDS.map(
      field => clip[field] ?? null,
    );
    // PLAT-11914 — soft line breaks change the rendered lines. Appended only
    // when the clip has some, so a clip without them hashes exactly as it did
    // before they existed (renders published then stay up to date). Sorted:
    // the order they were added in doesn't change the render.
    const tokenBreaks = [...(clip.softBreakAfterTokenIds ?? [])].sort();
    const phraseBreaks = [...(clip.softBreakPhrasePositions ?? [])]
      .sort((a, b) => a.phraseIndex - b.phraseIndex || a.offset - b.offset)
      .map(b => [b.phraseIndex, b.offset]);
    if (tokenBreaks.length > 0 || phraseBreaks.length > 0) {
      tuple.push(tokenBreaks, phraseBreaks);
    }
    return tuple;
  });

  const syntheticBreaks = (karaokeData.syntheticBreaks ?? []).map(b => [
    b.phraseOriginalIndices ?? null,
    b.textPosition ?? null,
  ]);

  // PLAT-11905 — a dubbed language's dub-timed set renders too, so an edit to
  // it must mark a dubbed render outdated. Added ONLY when the blob carries a
  // record, so a clip with no dub keeps exactly the hash it had.
  const dubTimed = karaokeData.dubTimed
    ? {
        dubTimed: karaokeData.dubTimed.rows.map(row => [
          row.parts.map(part => [part.segmentId, part.start, part.end]),
          row.startSecondsOffset ?? null,
          row.endSecondsOffset ?? null,
          row.karaokeConfigId ?? null,
        ]),
      }
    : {};

  return {
    config,
    defaultConfigId: karaokeData.defaultConfigId ?? null,
    clips,
    syntheticBreaks,
    ...dubTimed,
  };
}

/** Smallest source index a phrase covers — its stable ordering key. */
function phraseLead(p: TranslationPhrase): number {
  if (!p.originalIndices || p.originalIndices.length === 0) {
    return Number.MAX_SAFE_INTEGER;
  }
  let lead = p.originalIndices[0];
  for (const idx of p.originalIndices) if (idx < lead) lead = idx;
  return lead;
}

// ---------------------------------------------------------------------------
// Per-slice hashes
// ---------------------------------------------------------------------------

function hashTimeline(videoSource: Record<string, unknown>): string {
  return sha256(stableStringify(normalizeTimeline(videoSource)));
}

function hashCaptions(karaokeData: KaraokeData | null): string {
  return sha256(stableStringify(projectKaraoke(karaokeData)));
}

/**
 * Element-level transcript digest for staleness. Deliberately finer-grained than
 * `computeTranscriptHash` (a coarse topic-tagging cache key that is *stable*
 * across mid-paragraph edits): this must flip on ANY render-affecting change —
 * a word or casing edit anywhere in a paragraph, or a soft delete (`state` one
 * of `removed`/`muted`/`cut`), which stays in the array with an unchanged word
 * count and so slips past a count-based hash entirely.
 *
 * Projects each element, in array order, to only render-relevant fields that are
 * stable across representations: `value` (exact, case-sensitive), edit/inclusion
 * `state`, `speaker_label`, and `paragraph_id`. Incidental per-representation
 * fields (`id`, `source_uuid`, `initial_index`, `confidence`, clip-segment
 * pointers, and `ts`/`end_ts` — which null out on removal) are excluded so the
 * publish-time snapshot and the live editor state hash equal for identical
 * content. Empty ⇒ `[]`, a stable constant that only compares against itself.
 */
function hashTranscript(elements: TranscriptElement[]): string {
  const projected = (elements ?? []).map(el => [
    el.type,
    el.paragraph_id ?? '',
    el.value ?? '',
    el.state ?? '',
    el.speaker_label ?? '',
  ]);
  return sha256(JSON.stringify(projected));
}

function hashTranslation(phrases: TranslationPhrase[] | null): string {
  // Latest `translatedValue` (what karaoke renders) + `active` (retired
  // fragments don't render), ordered by source position — NOT the immutable
  // `originalTranslatedValue` / `originalValueSnapshot` baseline snapshots.
  const projected = (phrases ?? [])
    .filter(p => p.active !== false)
    .map(p => [phraseLead(p), p.translatedValue] as [number, string])
    .sort((a, b) => a[0] - b[0]);
  return sha256(JSON.stringify(projected));
}

function hashCompositionPhrases(
  entries: ReadonlyMap<string, CompositionPhraseTranslation>,
): string {
  const projected = Array.from(entries.entries())
    .map(([key, entry]) => [key, entry.translatedValue] as [string, string])
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
  return sha256(JSON.stringify(projected));
}

function hashScenes(scenes: IScene[] | null): string {
  return sha256(stableStringify(scenes ?? []));
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Compute the full per-field fingerprint for one `(AR × language)` cell. */
export function computePublishFingerprints(
  inputs: FingerprintInputs,
): PublishFingerprints {
  return {
    timeline: hashTimeline(inputs.videoSource),
    transcript: hashTranscript(inputs.transcriptElements),
    captions: hashCaptions(inputs.karaokeData),
    translation: hashTranslation(inputs.translationPhrases),
    compositionPhrases: hashCompositionPhrases(inputs.compositionPhrases),
    scenes: hashScenes(inputs.scenes),
  };
}

/**
 * The field keys whose stored hash differs from the current recomputed one.
 * Empty when the record is up to date or has no stored fingerprint.
 */
export function whyOutdated(
  record: PublishRecord,
  current: PublishFingerprints,
): (keyof PublishFingerprints)[] {
  const stored = record.fingerprintsJson;
  if (!stored) return [];
  return (Object.keys(current) as (keyof PublishFingerprints)[]).filter(
    key => stored[key] !== current[key],
  );
}

/**
 * Whether a record's render no longer reflects the current story state. Returns
 * `false` for records with no stored fingerprint (pre-fingerprint / pre-epic) —
 * they are "unknown", never flagged.
 */
export function isOutdated(
  record: PublishRecord,
  current: PublishFingerprints,
): boolean {
  if (!record.fingerprintsJson) return false;
  return whyOutdated(record, current).length > 0;
}
